import { createFileRoute, Outlet, useMatches } from '@tanstack/react-router';
import { useEffect } from 'react';

import { DuoChatList } from '@/features/chat/components/duoChatList';
import { NotificationsOffAlert } from '@/features/notifications/components/notificationsOffAlert';
import { useGetUnreadNotifications } from '@/features/notifications/hooks/useGetUnreadNotifications';
import { getUnreadNotificationsOptions } from '@/features/notifications/hooks/useGetUnreadNotifications/getUnreadNotifications.options';
import { useMarkNotificationsAsRead } from '@/features/notifications/hooks/useMarkNotificationsAsRead';
import { StreamChatUiProvider } from '@/integrations/getstream/stream-chat-ui-provider';
import { cn } from '@/shared/utils/cn';

export const Route = createFileRoute('/_protected/_app/_mainLayout/duo-chats')({
  component: RouteComponent,
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(
      getUnreadNotificationsOptions({ group: 'CHAT' }),
    );
  },
  head: () => ({
    meta: [{ title: 'Comms' }],
  }),
});

function RouteComponent() {
  const hasChildRoute = useMatches({
    // if the last match is not this route, we have a child route
    select: matches => matches.at(-1)?.routeId !== Route.id,
  });

  const { data: unreadCount } = useGetUnreadNotifications({
    group: 'CHAT',
    options: { select: data => data.page.totalElements },
  });
  const { mutate: markAsRead } = useMarkNotificationsAsRead();

  useEffect(() => {
    if (unreadCount > 0) {
      markAsRead('CHAT');
    }
  }, [unreadCount, markAsRead]);

  return (
    <StreamChatUiProvider>
      <div
        className={cn(
          'pb-navbar bg-chat-background flex h-full w-full flex-col',
          // avoid parent width restriction
          'lg:absolute lg:inset-0 lg:z-[calc(var(--navbar-z-index)-5)] lg:flex-row lg:overflow-hidden lg:pb-0 lg:pl-(--navbar-width)',
        )}
      >
        {/* Left column: full chat list (hidden on mobile when viewing chat/error) */}
        <div
          className={cn(
            'flex h-full w-full flex-col gap-10 overflow-x-hidden overflow-y-auto px-4 pt-[calc(var(--safe-top)+--spacing(4))]',
            'lg:w-90 lg:shrink lg:border-r-2 xl:w-100',
            hasChildRoute && 'hidden lg:flex',
          )}
        >
          <NotificationsOffAlert />
          <DuoChatList />
        </div>

        {/* Child route (chat/error): flows below New Matches on mobile */}
        {hasChildRoute && (
          <div
            className={cn(
              'absolute inset-0 z-[calc(var(--navbar-z-index)+5)] overflow-hidden pt-(--safe-top) pb-(--safe-bottom)',
              'lg:relative lg:z-0 lg:grow lg:pt-0 lg:pb-0',
            )}
          >
            <Outlet />
          </div>
        )}
      </div>
    </StreamChatUiProvider>
  );
}
